import { DealNoteResponse, NoteView } from '../../types/Note';
import { IPitchStatusState } from '../../types/PitchStatus';
import { BuildPathFromParameters } from '../api/ApiMethods';
import {
  GetMultiFromPath,
  GetOneFromPath,
  Post,
  PostWithVoidReturn,
  Put,
} from '../api/GenericClient';

const initialPath = '/common';

async function getNotes(leadId: string, dealerId: string): Promise<NoteView[]> {
  const notesPath = `/notes/${leadId}`;
  let apiPath = initialPath.concat(notesPath);
  const queryData = { dealerId: dealerId };
  apiPath = BuildPathFromParameters(apiPath, queryData);

  const response = await GetMultiFromPath<DealNoteResponse>(apiPath);
  return transformNotes(response);
}

async function getNote(noteId: number, dealerId: string): Promise<NoteView> {
  const notesPath = `/notes/single/${noteId}`;
  let apiPath = initialPath.concat(notesPath);
  const queryData = { dealerId: dealerId };
  apiPath = BuildPathFromParameters(apiPath, queryData);

  const response = await GetOneFromPath<DealNoteResponse>(apiPath);
  return transformNotes([response])[0];
}

function transformNotes(data: Array<DealNoteResponse>): Array<NoteView> {
  const result: Array<NoteView> = [];
  data.forEach((v) => {
    result.push({
      content: v.notes
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/<BR\s*\/?>|<br\s*\/?>/g, ' \n'),
      createdAt: v.xEntity_InsertedAt,
      createdBy: v.username,
      offerNotePushRetry: v.offerNotePushRetry,
      source: v.source,
      warning: v.warning,
      id: v.id,
      crmStatus: v.crmStatus,
      crmType: v.crmType,
    });
  });
  return result;
}

function sanitize(note) {
  return note.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

async function postNotes(
  leadId: string,
  noteInput: string,
  dealerId: string,
  vin: string,
): Promise<NoteView[]> {
  const notesPath = '/notes';
  let apiPath = initialPath.concat(notesPath);
  const sanitizedNote = sanitize(noteInput);
  const queryData = { dealerId: dealerId };
  apiPath = BuildPathFromParameters(apiPath, queryData);

  const noteBody = {
    closedDealId: leadId,
    notes: sanitizedNote,
    vin: vin,
  };

  const response = await Post<Array<DealNoteResponse>>(apiPath, noteBody);
  return transformNotes(response);
}

async function createSelectedPitchStatus(
  pitchStatusVal: string,
  closedDealId: string,
  vin: string,
  dealerId: string,
  selectedPitchStatus: IPitchStatusState,
): Promise<any> {
  const apiPath = initialPath.concat('/pitchstatus');
  const queryData = {
    pitchStatus: pitchStatusVal,
    closedDealId: closedDealId,
    vin: vin,
    dealerId: dealerId,
  };
  const path = BuildPathFromParameters(apiPath, queryData);

  const statusCode =
    selectedPitchStatus.subStatuses.length > 0
      ? selectedPitchStatus.subStatuses[0].index
      : selectedPitchStatus.index;
  const statusDisplayName = selectedPitchStatus.name;
  const subStatusDisplayName = selectedPitchStatus.subStatuses[0].name;
  const body = { statusCode, statusDisplayName, subStatusDisplayName };

  return await Put<boolean>(path, body);
}

async function retryNotePush(
  noteId: string | number,
  dealerId: string,
  addMissingData: boolean = false,
): Promise<void> {
  let apiPath = initialPath.concat(`/note/${noteId}/retry`);
  const queryData = { dealerId: dealerId, addMissingData: addMissingData.toString() };
  apiPath = BuildPathFromParameters(apiPath, queryData);
  return await PostWithVoidReturn(apiPath, null);
}

export const DealNoteService = {
  getNotes,
  postNotes,
  createSelectedPitchStatus,
  retryNotePush,
  getNote,
};
